import type { ScanAEnvoyer } from '../api/reserve.js';
import { estApiError } from '../api/errors.js';

export interface PorteeScans {
  compteId: string;
  galerie: string;
  campagneId: string;
}
interface ScanEnFile extends PorteeScans {
  scan: ScanAEnvoyer;
}
const memePortee = (a: PorteeScans, b: PorteeScans) =>
  a.compteId === b.compteId && a.galerie === b.galerie && a.campagneId === b.campagneId;

/** La clé du scan survit au rejeu, y compris après une réponse réseau perdue. */
export function creerFileHorsLigne(fabrique?: IDBFactory) {
  const memoire = new Map<string, ScanEnFile>();
  let base: Promise<IDBDatabase> | undefined;
  let indisponible = false;
  const rejeux = new Map<string, Promise<string[]>>();
  async function magasin<T>(
    mode: IDBTransactionMode,
    faire: (magasin: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T | undefined> {
    const moteur = fabrique ?? globalThis.indexedDB;
    if (!moteur || indisponible) return undefined;
    try {
      base ??= new Promise((resoudre, rejeter) => {
        const requete = moteur.open('artosera-scans-inventaire', 1);
        requete.onupgradeneeded = () =>
          requete.result.createObjectStore('scans', { keyPath: 'scan.cleIdempotence' });
        requete.onsuccess = () => resoudre(requete.result);
        requete.onerror = () => rejeter(requete.error);
        requete.onblocked = () => rejeter(new Error('La file de scans est indisponible.'));
      });
      const db = await base;
      return await new Promise<T>((resoudre, rejeter) => {
        const tx = db.transaction('scans', mode);
        const requete = faire(tx.objectStore('scans'));
        tx.oncomplete = () => resoudre(requete.result);
        tx.onabort = () => rejeter(tx.error);
        tx.onerror = () => rejeter(tx.error);
      });
    } catch {
      indisponible = true;
      return undefined;
    }
  }
  async function charger(): Promise<ScanEnFile[]> {
    const lignes = (await magasin('readonly', (m) => m.getAll())) as ScanEnFile[] | undefined;
    if (lignes) {
      memoire.clear();
      for (const ligne of lignes) memoire.set(ligne.scan.cleIdempotence, ligne);
      return lignes;
    }
    return [...memoire.values()];
  }
  async function lister(portee: PorteeScans): Promise<ScanAEnvoyer[]> {
    return (await charger())
      .filter((ligne) => memePortee(ligne, portee))
      .map((ligne) => ligne.scan);
  }
  async function retirer(cle: string) {
    await magasin('readwrite', (m) => m.delete(cle));
    memoire.delete(cle);
  }
  return {
    lister,
    async portees(compteId: string, galerie: string): Promise<PorteeScans[]> {
      const campagnes = new Map<string, PorteeScans>();
      for (const ligne of await charger()) {
        if (ligne.compteId === compteId && ligne.galerie === galerie)
          campagnes.set(ligne.campagneId, { compteId, galerie, campagneId: ligne.campagneId });
      }
      return [...campagnes.values()];
    },
    async ajouter(portee: PorteeScans, code: string): Promise<ScanAEnvoyer> {
      const scan = {
        code,
        cleIdempotence: crypto.randomUUID(),
        scanneLe: new Date().toISOString(),
      };
      const ligne = { ...portee, scan };
      memoire.set(scan.cleIdempotence, ligne);
      await magasin('readwrite', (m) => m.put(ligne));
      return scan;
    },
    rejouer(
      portee: PorteeScans,
      envoyer: (scan: ScanAEnvoyer) => Promise<unknown>,
    ): Promise<string[]> {
      const cle = JSON.stringify(portee);
      const enCours = rejeux.get(cle);
      if (enCours) return enCours;
      const execution = (async () => {
        const refus: string[] = [];
        let scan = (await lister(portee))[0];
        while (scan) {
          try {
            await envoyer(scan);
          } catch (erreur) {
            if (!estApiError(erreur) || erreur.status < 400 || erreur.status >= 500) break;
            refus.push(erreur.message);
          }
          await retirer(scan.cleIdempotence);
          scan = (await lister(portee))[0];
        }
        return refus;
      })().finally(() => rejeux.delete(cle));
      rejeux.set(cle, execution);
      return execution;
    },
  };
}
export const fileHorsLigne = creerFileHorsLigne();
